// shared/src/resolvers/llm-string-coerce.ts
// 2026-06-11 — translate LLM-emitted STRING-LIST items to plain strings at the
// boundary, instead of hard-failing the model against a rigid z.array(z.string()).
//
// Output-side sibling of llm-enum-coerce.ts. Same standing rule: NEVER hard-fail
// a whole brief because the model returned a valid-but-richer shape. For "list of
// short labels/points" fields (trajectory_breaks_detected, cascade_nodes,
// cross_fed_counter, citations, tickers) the model intermittently emits an array
// of {indicator/label, description}-style OBJECTS instead of plain strings — which
// a rigid z.array(z.string()) rejects.
//
// The trigger: the 2026-06-10T19:00Z scheduled brief died at m.inductive.forecast
// because DeepSeek returned trajectory_breaks_detected as
//   [{indicator:"USO", description:"..."}, {indicator:"TNX", description:"..."}]
// One cosmetic shape difference killed a 3.5-minute brief. See
// docs/audits/LEARNINGS.md (the C029-class enum twin) +
// docs/llm-string-list-coercion-2026-06-11.md.
//
// Fail-loud preserved (per [[feedback_no_fallbacks]]): a `null`/`undefined` item
// passes through UNCHANGED so the wrapping z.string() still rejects it — we
// stringify KNOWN object/scalar shapes, we never invent content for a genuinely
// broken item. The signal is preserved (never "[object Object]", never silently
// dropped); unrecognized object shapes JSON.stringify so the raw survives.

import { z } from 'zod';

// Common "label" + "detail" keys the model uses when it wraps a list item as an
// object. First hit wins; the two are joined "LABEL: detail".
const LABEL_KEYS: readonly string[] = [
  'indicator', 'ticker', 'symbol', 'name', 'label', 'node', 'point',
  'term', 'title', 'source', 'event', 'item', 'key', 'id',
];
const DETAIL_KEYS: readonly string[] = [
  'description', 'detail', 'reason', 'explanation', 'text', 'note',
  'rationale', 'effect', 'value', 'summary', 'quote',
];

function pickStr(o: Record<string, unknown>, keys: readonly string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return '';
}

/**
 * Coerce one LLM list item to a string (or pass it through for the wrapping
 * z.string() to fail-loud on). Strings pass unchanged; numbers/booleans
 * String()-ify; objects become "LABEL: detail" (or JSON.stringify when no
 * label/detail key matches). null/undefined pass through unchanged → fail-loud.
 */
export function coerceLlmStringItem(v: unknown): unknown {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v !== null && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const label  = pickStr(o, LABEL_KEYS);
    const detail = pickStr(o, DETAIL_KEYS);
    const joined = [label, detail].filter(Boolean).join(': ');
    if (joined) return joined;
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return v;   // null / undefined → wrapping z.string() fail-louds (genuinely broken)
}

/**
 * Drop-in replacement for `z.string()` as an ARRAY ELEMENT in LLM-output
 * schemas: `z.array(llmStringItem)` accepts string OR object items and yields
 * `string[]`. Chain the array constraints at the call site, e.g.
 * `z.array(llmStringItem).min(1).max(5)`. z.infer is `string`, so downstream
 * typed consumers are unchanged.
 */
export const llmStringItem = z.preprocess(coerceLlmStringItem, z.string());
